import Vue from 'vue';
import { RunnitNodeDef, RunnitNodeRun } from '@run-diffusion/shared';
import { db } from '@/firebase';
import {
	isPartialLibraryNodeRun,
	isResolvedNodeDef,
	mergeHydratedNodeRun,
} from '@/utils/libraryApi';

/**
 * Lazy Firestore hydration for runs that arrived from the Library REST endpoint.
 *
 * A filtered library view is served from Postgres (ADR 0018), and `fields` /
 * `inputs` carry no `@sqlsync` marker — they are not columns at all. Rather than
 * sync a large JSON blob onto every row for data only needed when a result is
 * inspected, the run doc is fetched on demand and cached per id.
 *
 * This lives in a mixin because BOTH surfaces that render generation details
 * need it: the carousel dialog, and the inline info card in the gallery's INFO
 * display mode. Hydrating in only one of them is what left the INFO-mode card
 * permanently blank in filtered views while the same result showed full details
 * once opened in the dialog.
 *
 * Consumers render `hydratedRunFor(run)` and call `hydratePartialNodeRun(run)`
 * when the run becomes visible. Both are no-ops for a non-partial run, so
 * callers do not need to branch.
 */
export const LibraryRunHydrationMixin = Vue.extend({
	data () {
		return {
			// runId → the raw Firestore doc, or null while a fetch is in flight.
			// The key's PRESENCE is the dedupe token, so an in-flight entry must
			// stay null rather than be deleted.
			hydratedNodeRunsById: {} as Record<string, RunnitNodeRun | null>,
		};
	},
	methods: {
		/**
		 * Merge the cached Firestore doc over a REST partial. Re-merges against the
		 * live run every call (spread last) so a fresh prop/list update is not
		 * shadowed by the cache. Returns the run untouched when nothing is cached.
		 */
		hydratedRunFor (nodeRun: RunnitNodeRun): RunnitNodeRun {
			const hydrated = nodeRun?.id ? this.hydratedNodeRunsById[nodeRun.id] : null;
			return hydrated ? mergeHydratedNodeRun(hydrated, nodeRun) : nodeRun;
		},
		/**
		 * Override in a consumer that needs to react once a run finishes hydrating
		 * (the carousel recomputes its comparison URLs). Default is a no-op.
		 */
		async onNodeRunHydrated (_nodeRun: RunnitNodeRun): Promise<void> {
			// no-op
		},
		async hydratePartialNodeRun (nodeRun: RunnitNodeRun): Promise<void> {
			if (!isPartialLibraryNodeRun(nodeRun) || !nodeRun.id) return;
			// Claim the id before the first await: the entry's presence is what
			// dedupes concurrent callers (watchers that fire in the same flush, or
			// a list re-render).
			if (nodeRun.id in this.hydratedNodeRunsById) return;
			this.$set(this.hydratedNodeRunsById, nodeRun.id, null);

			try {
				const snapshot = await db.collection('runnitNodeRuns').doc(nodeRun.id).get();
				// Missing doc: leave the null entry so we never refetch it.
				if (!snapshot.exists) return;
				const hydratedRun = snapshot.data() as RunnitNodeRun;
				// The run doc carries nodeDef only as a reference, so resolve the full
				// tool (its `type` and `fields`, which the REST tools shape omits
				// and which canBeFeatured + the info panel read). mergeHydratedNodeRun
				// then prefers this over the thin REST nodeDef.
				if (!isResolvedNodeDef(hydratedRun.nodeDef) && hydratedRun.nodeDefId) {
					try {
						const nodeDefSnap = await db.doc(`runnitNodeDefs/${hydratedRun.nodeDefId}`).get();
						if (nodeDefSnap.exists) {
							hydratedRun.nodeDef = {
								...(nodeDefSnap.data() as RunnitNodeDef),
								id: hydratedRun.nodeDefId,
							} as RunnitNodeDef;
						}
					} catch (err) {
						console.error('Failed to resolve nodeDef during hydration', err);
					}
				}
				// Cache the RAW (nodeDef-resolved) doc, not a merged run — callers
				// re-merge against the live run so later updates are not shadowed.
				this.$set(this.hydratedNodeRunsById, nodeRun.id, hydratedRun);
				await this.onNodeRunHydrated(nodeRun);
			} catch (error) {
				// Drop the entry so a later view retries — a transient failure must
				// not be cached as permanently unhydratable.
				this.$delete(this.hydratedNodeRunsById, nodeRun.id);
				console.error('Failed to hydrate library node run', error);
			}
		},
		/** Hydrate every partial in a list (the INFO-mode card renders one per run). */
		hydratePartialNodeRuns (nodeRuns: RunnitNodeRun[]): void {
			(nodeRuns || []).forEach((run) => {
				if (isPartialLibraryNodeRun(run)) this.hydratePartialNodeRun(run);
			});
		},
	},
});
